"use client";

import { forwardRef } from "react";
import NextLink from "next/link";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Typography } from "./Typography";

const linkVariants = cva(
  "inline-flex items-center transition-colors focus:outline-none",
  {
    variants: {
      variant: {
        default: "text-primary-light hover:text-primary",
        primary: "text-light bg-primary hover:bg-primary-light px-3 py-2 rounded-md",
        secondary: "text-light bg-secondary hover:bg-secondary-light px-3 py-2 rounded-md",
        ghost: "hover:text-primary",
        underline: "text-primary-light hover:text-primary underline",
      },
      size: {
        sm: "text-sm",
        md: "text-base",
        lg: "text-lg",
      },
      weight: {
        normal: "font-normal",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      weight: "medium",
    },
  }
);

export interface LinkProps
  extends React.ComponentPropsWithoutRef<typeof NextLink>,
    VariantProps<typeof linkVariants> {
  asChild?: boolean;
  textVariant?: VariantProps<typeof Typography>["variant"];
  textClassNames?: string;
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ 
    className, 
    variant, 
    size, 
    weight,
    asChild = false, 
    href, 
    children,
    textVariant = "span",
    textClassNames = "",
    ...props 
  }, ref) => {
    const Comp = asChild ? Slot : NextLink;

    return (
      <Comp
        className={cn(linkVariants({ variant, size, weight, className }))}
        ref={ref}
        href={href}
        {...props}
      >
        {typeof children === 'string' ? (
          <Typography
            as="span"
            variant={textVariant}
            weight={weight}
            className={textClassNames}
          >
            {children}
          </Typography>
        ) : (
          children
        )}
      </Comp>
    );
  }
);

Link.displayName = "Link";

export { Link, linkVariants };
